import { useQuery } from '@tanstack/react-query'
import { Lookup } from '../../design/index.js'
import { useApi } from '../../app/api.js'

/**
 * Several records of one master, chosen one at a time with the ordinary
 * type-ahead and shown as removable chips. Stored ids get their names from
 * the lookup endpoint, so a chip never shows a uuid for long.
 */
export const MultiLookup = ({ entity, value, onChange, placeholder }: {
  entity: string
  value: readonly string[]
  onChange: (next: string[]) => void
  placeholder?: string
}) => {
  const api = useApi()
  const names = useQuery({
    queryKey: ['lookupNames', api.companyId, entity, [...value].sort().join(',')],
    enabled: value.length > 0,
    queryFn: async () => {
      const out: Record<string, string> = {}
      for (const id of value) {
        const rows = await api.send<ReadonlyArray<{ id: string; label: string | null }>>(
          `/lookups/${encodeURIComponent(entity)}?id=${encodeURIComponent(id)}`,
        )
        out[id] = rows.find((r) => r.id === id)?.label ?? id
      }
      return out
    },
    staleTime: 60_000,
  })
  return (
    <div>
      <Lookup
        entity={entity}
        value=""
        placeholder={placeholder ?? 'Start typing to add'}
        onChange={(id) => {
          if (!id || value.includes(id)) return
          onChange([...value, id])
        }}
      />
      {value.length > 0 ? (
        <div className="row row--wrap" style={{ marginTop: 8 }}>
          {value.map((id) => (
            <button key={id} type="button" className="chip chip--removable"
              onClick={() => onChange(value.filter((x) => x !== id))}>
              {names.data?.[id] ?? '…'} <span aria-hidden="true">×</span>
              <span className="sr-only">Remove</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** Choices from a short list, ticked as chips. */
export const ChipChoice = ({ options, value, onChange, label }: {
  options: ReadonlyArray<{ value: string; label: string }>
  value: readonly string[]
  onChange: (next: string[]) => void
  label: string
}) => (
  <div className="row row--wrap" role="group" aria-label={label}>
    {options.map((o) => {
      const on = value.includes(o.value)
      return (
        <button key={o.value} type="button" aria-pressed={on}
          className={on ? 'chip chip--removable chip--on' : 'chip chip--removable'}
          onClick={() => onChange(on ? value.filter((x) => x !== o.value) : [...value, o.value])}>
          {o.label}
        </button>
      )
    })}
    {options.length === 0 ? <span className="muted">Nothing to choose from yet</span> : null}
  </div>
)
